import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Select from '../../shared/reactSelect/select.component';
import SecondaryMenu from '../../menu/secondaryMenu.component';
import { withPermissions, permissions } from '../../shared/authorization';
import { ReduxAlert } from '../../shared/alert.component';

import { PeriodPropTypes } from '../../shared/propTypes/period';
import { periodOrCurrentSelector, environmentSuffixSelector } from '../../shared/store/selectors';

import {
  fetchInitialDataForBeginningBalances,
  selectPeriodForBeginningBalances,
  selectCompanyCodeForBeginningBalances,
  selectConnectionForBeginningBalances,
  selectCompanyCodeForBeginningBalancesConnection,
  showImportBeginningBalancesModal,
  showImportBatchBeginningBalancesModal,
} from './store/actions';
import { hideImportBeginningBalancesValidationModal } from '../../shared/store/actions';

import {
  adjustmentPeriodSelector,
  adjustmentPeriodsOptionsSelector,
  beginningBalancesImportDataSelector,
  beginningBalancesLoadingSelector,
  beginningBalancesSelector,
  cannotImportBeginningBalancesSelector,
  companyCodeForConnectionSelector,
  companyCodeSelector,
  companyCodesForConnectionForDropdownSelector,
  companyCodesSelector,
  connectionSelector,
  connectionsSelector,
  validationSummarySelector,
  companyCodeWithCurrencySelector,
  companyCodesForConnectionSelector,
  isLoadingCompanyCodesSelector,
  isLoadingConnectionsSelector,
  isLoadingCompanyCodesForConnectionSelector,
  beginningBalancesIgnoredCompanyCodesSelector,
} from './store/selectors';
import {
  connectionTemplatesDetailsPropType,
  beginningBalancesPropType,
  companyCodePropType,
  validationSummaryPropType,
} from './propTypes';

import BeginningBalancesGrid from './beginningBalancesGrid.components';
import SummaryTextBox from '../../shared/summaryTextBox/summaryTextBox.component';
import HamburgerDropdown from './hamburgerDropdown.component';
import ModalNames from './modalNames';
import { modalDataSelector } from '../../shared/modals/modals.selectors';
import ImportBeginningBalancesModal from './importBeginningBalancesModal.component';
import ImportBatchBeginningBalancesModal from './importBatchBeginningBalancesModal.component';
import ImportBeginningBalanceValidationModal from './importBeginningBalanceValidationModal.component';

class BeginningBalancesPage extends PureComponent {
  static propTypes = {
    fetchInitialDataForBeginningBalances: PropTypes.func.isRequired,
    adjustmentPeriods: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired).isRequired,
    adjustmentPeriod: PropTypes.string,
    companyCodes: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired).isRequired,
    companyCode: PropTypes.string,
    companyCodeData: companyCodePropType,
    selectPeriodForBeginningBalances: PropTypes.func.isRequired,
    connections: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    connection: PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
    selectCompanyCodeForBeginningBalances: PropTypes.func.isRequired,
    selectConnectionForBeginningBalances: PropTypes.func.isRequired,
    selectCompanyCodeForBeginningBalancesConnection: PropTypes.func.isRequired,
    companyCodesForConnectionForDropdown: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired).isRequired,
    companyCodeForConnection: PropTypes.string,
    beginningBalancesLoading: PropTypes.bool.isRequired,
    beginningBalances: PropTypes.arrayOf(beginningBalancesPropType),
    importData: PropTypes.arrayOf(PropTypes.shape({
      companyCode: PropTypes.string.isRequired,
      connName: PropTypes.string.isRequired,
      importResults: PropTypes.string.isRequired,
      importStatus: PropTypes.number.isRequired,
      results: PropTypes.string.isRequired,
    })),
    importIgnoredCompanyCodes: PropTypes.arrayOf(PropTypes.string),
    validationSummary: validationSummaryPropType,

    canModify: PropTypes.bool.isRequired,
    cannotImportBeginningBalances: PropTypes.bool.isRequired,

    showImportBeginningBalancesModal: PropTypes.func.isRequired,
    showImportBatchBeginningBalancesModal: PropTypes.func.isRequired,
    importBeginningBalancesModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
    }).isRequired,
    importBatchBeginningBalancesModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
    }).isRequired,
    period: PeriodPropTypes.isRequired,
    companyCodesForConnection: connectionTemplatesDetailsPropType,
    environmentSuffix: PropTypes.string.isRequired,

    importBeginningBalanceValidationModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: PropTypes.shape({
        validationErrors: PropTypes.objectOf(PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))),
      }),
    }).isRequired,
    hideImportBeginningBalancesValidationModal: PropTypes.func.isRequired,

    isLoadingCompanyCodes: PropTypes.bool,
    isLoadingConnections: PropTypes.bool,
    isLoadingCompanyCodesForConnection: PropTypes.bool,
  };

  componentDidMount() {
    this.props.fetchInitialDataForBeginningBalances();
  }

  selectPeriod = selectedPeriod => {
    const { selectPeriodForBeginningBalances } = this.props;
    selectPeriodForBeginningBalances(selectedPeriod.value);
  };

  selectConnection = connection => {
    const { adjustmentPeriod, selectConnectionForBeginningBalances } = this.props;
    selectConnectionForBeginningBalances(adjustmentPeriod, connection);
  };

  selectCompanyCode = selectedCompanyCode => {
    const { selectCompanyCodeForBeginningBalances } = this.props;
    selectCompanyCodeForBeginningBalances(selectedCompanyCode.value);
  };

  selectCompanyCodeViaConnection = selectedCompanyCode => {
    const { selectCompanyCodeForBeginningBalancesConnection } = this.props;
    selectCompanyCodeForBeginningBalancesConnection(selectedCompanyCode.value);
  };

  importStatus = () => {
    const { importData, importIgnoredCompanyCodes } = this.props;

    const totalImportedCount = importData.length;
    const erroneousImportedCount = importData.filter(
      value => value.importStatus === -1
    ).length;

    let importStatusMessage = `${totalImportedCount} Beginning Balances Imports.
      ${erroneousImportedCount} Beginning Balances Imports with Issues.
    `;

    if (importData.length) {
      importData.forEach(importedBalance => {
        importStatusMessage = `${importStatusMessage }
          ${importedBalance.results}
        `;
      });
    }

    if (importIgnoredCompanyCodes.length) {
      importStatusMessage = `${importStatusMessage }

        Company Code records not imported in the last import file: ${importIgnoredCompanyCodes.join(', ')}
      `;
    }

    return importStatusMessage;
  };

  render() {
    const {
      adjustmentPeriod,
      adjustmentPeriods,
      companyCodes,
      companyCode,
      connections,
      connection,
      companyCodeData,
      companyCodesForConnectionForDropdown,
      companyCodeForConnection,
      beginningBalances,
      beginningBalancesLoading,
      validationSummary,
      canModify,
      cannotImportBeginningBalances,
      showImportBeginningBalancesModal,
      showImportBatchBeginningBalancesModal,
      importBeginningBalancesModalData,
      importBatchBeginningBalancesModalData,
      companyCodesForConnection,
      environmentSuffix,
      importBeginningBalanceValidationModalData,
      hideImportBeginningBalancesValidationModal,
      isLoadingCompanyCodes,
      isLoadingConnections,
      isLoadingCompanyCodesForConnection,
      importIgnoredCompanyCodes,
    } = this.props;


    return (
      <div className="beginning-balances-page">
        <SecondaryMenu>
          <Select
            className="select-period"
            clearable={false}
            name="period"
            options={adjustmentPeriods}
            onChange={this.selectPeriod}
            scrollMenuIntoView={false}
            value={adjustmentPeriod}
          />
          <Select
            className="select-company-min"
            clearable={false}
            name="companyCode"
            options={companyCodes}
            onChange={this.selectCompanyCode}
            scrollMenuIntoView={false}
            value={!connection ? companyCode : null}
            placeholder="Select a Provision"
            virtualized
            isLoading={isLoadingCompanyCodes}
          />
          <span>or</span>
          <Select
            className="select-connection"
            clearable={false}
            name="connection"
            options={connections}
            onChange={this.selectConnection}
            scrollMenuIntoView={false}
            value={connection}
            placeholder="Select a Connection"
            virtualized
            isLoading={isLoadingConnections}
          />
          {connection
            && <Select
              className="select-company-min select-filtered-company"
              clearable={false}
              name="companyCodeForConnection"
              options={companyCodesForConnectionForDropdown}
              onChange={this.selectCompanyCodeViaConnection}
              scrollMenuIntoView={false}
              value={companyCodeForConnection}
              placeholder="Select a Provision"
              isLoading={isLoadingCompanyCodesForConnection}
              virtualized
            />
          }
          <div className="hamburger-dropdown ml-auto text-right">
            <HamburgerDropdown
              cannotImportBeginningBalances={!canModify || cannotImportBeginningBalances}
              disableImportBeginningBalancesButton={!companyCode}
              disableImportBatchBeginningBalancesButton={!connection}
              companyCodeData={companyCodeData}
              adjustmentPeriod={adjustmentPeriod}
              showImportBeginningBalancesModal={showImportBeginningBalancesModal}
              showImportBatchBeginningBalancesModal={showImportBatchBeginningBalancesModal}
              connectionTemplateDetails={companyCodesForConnection}
              connection={(connection && connection.label) || ''}
              environmentSuffix={environmentSuffix}
            />
          </div>
        </SecondaryMenu>
        <div className="page">
          <div className="container-fluid">
            <ReduxAlert/>
            <div className="row no-gutters page-content-container">
              <div className="col">
                <div className="d-flex flex-row justify-content-between">
                  <h5>Beginning Balances</h5>
                </div>
              </div>
            </div>
            <div className="row no-gutters page-content-container">
              <div className="col">
                {companyCode || companyCodeForConnection
                  ? <BeginningBalancesGrid
                    companyCode={companyCodeData}
                    beginningBalances={beginningBalances}
                    beginningBalancesLoading={beginningBalancesLoading}
                    validationSummary={validationSummary}
                    importIgnoredCompanyCodes={importIgnoredCompanyCodes}
                  />
                  : null
                }
                {connection && !companyCode && !companyCodeForConnection
                  ? <SummaryTextBox
                    content={this.importStatus()}
                  />
                  : null
                }
              </div>
            </div>
          </div>
        </div>
        <ImportBeginningBalancesModal
          companyCode={companyCode}
          isVisible={importBeginningBalancesModalData.show}
        />
        <ImportBatchBeginningBalancesModal
          connection={connection}
          isVisible={importBatchBeginningBalancesModalData.show}
        />
        <ImportBeginningBalanceValidationModal
          data={importBeginningBalanceValidationModalData.data}
          isVisible={importBeginningBalanceValidationModalData.show}
          hideImportBeginningBalancesValidationModal={hideImportBeginningBalancesValidationModal}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    adjustmentPeriod: adjustmentPeriodSelector(state),
    adjustmentPeriods: adjustmentPeriodsOptionsSelector(state),
    beginningBalances: beginningBalancesSelector(state),
    beginningBalancesLoading: beginningBalancesLoadingSelector(state),
    cannotImportBeginningBalances: cannotImportBeginningBalancesSelector(state),
    companyCode: companyCodeSelector(state),
    companyCodeData: companyCodeWithCurrencySelector(state),
    companyCodes: companyCodesSelector(state),
    companyCodeForConnection: companyCodeForConnectionSelector(state),
    companyCodesForConnection: companyCodesForConnectionSelector(state),
    companyCodesForConnectionForDropdown: companyCodesForConnectionForDropdownSelector(state),
    connection: connectionSelector(state),
    connections: connectionsSelector(state),
    importData: beginningBalancesImportDataSelector(state),
    importIgnoredCompanyCodes: beginningBalancesIgnoredCompanyCodesSelector(state),
    validationSummary: validationSummarySelector(state),
    importBeginningBalancesModalData: modalDataSelector(state, ModalNames.ImportBeginningBalances),
    importBatchBeginningBalancesModalData: modalDataSelector(state, ModalNames.ImportBatchBeginningBalances),
    importBeginningBalanceValidationModalData: modalDataSelector(
      state,
      ModalNames.ImportBatchBeginningBalancesValidation
    ),
    period: periodOrCurrentSelector(state),
    environmentSuffix: environmentSuffixSelector(state),
    isLoadingCompanyCodes: isLoadingCompanyCodesSelector(state),
    isLoadingConnections: isLoadingConnectionsSelector(state),
    isLoadingCompanyCodesForConnection: isLoadingCompanyCodesForConnectionSelector(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchInitialDataForBeginningBalances,
    selectPeriodForBeginningBalances,
    selectCompanyCodeForBeginningBalances,
    selectConnectionForBeginningBalances,
    selectCompanyCodeForBeginningBalancesConnection,
    showImportBeginningBalancesModal,
    showImportBatchBeginningBalancesModal,
    hideImportBeginningBalancesValidationModal,
  }
)(withPermissions(permissions.BEGINNING_BALANCES)(BeginningBalancesPage));
